import { ActionIcon, Tooltip, Divider, Title, Group, useMantineColorScheme, Box } from "@mantine/core";
import { Menu } from "@mantine/core";
import { IconAccessPoint, IconLogout, IconUser, IconLanguage, IconChevronRight, IconMoon, IconSun } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useTRPC } from "../../trpc";
import { useCallback } from "react";
import { Avatar } from "../../components/Avatar";
import { FormattedMessage, useIntl } from "react-intl";
import { useSetUser, useUser } from "../../hooks/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { captureEvent } from "../../utils/posthog";
import { Event } from "../../constants/events";
import { type Locale, supportedLocalesOptions } from "@project/common";
import { useSetLocale } from "../../hooks/locale.js";
import { setInitialTheme } from "@project/common";

export const AvatarControl = () => {
  const intl = useIntl();
  const user = useUser();
  const trpc = useTRPC();
  const setUser = useSetUser();
  const navigate = useNavigate();
  const setLocale = useSetLocale();
  const queryClient = useQueryClient();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const handleLogout = useCallback(() => {
    captureEvent(Event.AuthLogout);
    void navigate({ to: "/sign-in" }).then(() => {
      setUser(null);
    });
  }, [navigate, setUser]);

  const { mutateAsync: logout } = useMutation(trpc.logout.mutationOptions());
  const { mutateAsync: changeLocale } = useMutation(trpc.changeLocale.mutationOptions());

  if (!user) {
    return null;
  }

  const handleLocaleChange = (value: Locale) => {
    setLocale(value);
    void changeLocale(value)
      .then((updated) => {
        queryClient.setQueryData(trpc.getCurrentUser.queryKey(), updated);
      });
  };

  const label = intl.formatMessage({ defaultMessage: "User options", id: "label.userOptions" });
  const nextTheme = colorScheme === "dark" ? "light" : "dark";
  const ICON_SIZE = 24;

  return (
    <Menu
      shadow="md"
      position="right-end"
    >
      <Menu.Target>
        <Tooltip label={label}>
          <ActionIcon variant="default" aria-label={label} size="xl" radius="xl">
            <Avatar
              user={user}
              size="48"
            />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Group justify="space-between" gap="xs" p="xs">
          <Avatar user={user} size="32" />
          <Box>
            <Title fw="inherit" order={5}>
              {user.name}
            </Title>
            <Title fw="inherit" order={6} c="gray.6">
              {user.email}
            </Title>
          </Box>
        </Group>

        <Divider />

        {user.currentProvider.emailVerified && (
          <Menu.Item
            leftSection={<IconUser size={ICON_SIZE} />}
            onClick={() => {
              void navigate({
                to: "/profile",
              });
            }}
          >
            <FormattedMessage id="menu.profile" defaultMessage="Profile" />
          </Menu.Item>
        )}

        {user.accounts.length > 1 && (
          <Menu.Item
            onClick={() => {
              void navigate({
                to: "/select-account",
              });
            }}
            leftSection={<IconAccessPoint size={ICON_SIZE} />}
          >
            <FormattedMessage id="menu.changeAccount" defaultMessage="Change account" />
          </Menu.Item>
        )}

        <Menu
          closeOnItemClick={false}
          withinPortal={false}
          trigger="click-hover"
          position="right-start"
          offset={8}
          styles={{
            dropdown: {
              boxShadow: "none",
            },
          }}
        >
          <Menu.Target>
            <Menu.Item
              leftSection={<IconLanguage size={ICON_SIZE} />}
              rightSection={<IconChevronRight size={16} />}
            >
              <FormattedMessage id="menu.languageOptions" defaultMessage="Language" />
            </Menu.Item>
          </Menu.Target>
          <Menu.Dropdown>
            {supportedLocalesOptions.map(({ localLabel, value }, index) => {
              return (
                <Menu.Item
                  key={index}
                  onClick={() => {
                    handleLocaleChange(value);
                  }}
                >
                  {localLabel}
                </Menu.Item>
              );
            })}
          </Menu.Dropdown>
        </Menu>

        <Menu.Item
          leftSection={
            nextTheme === "dark" ?
                <IconMoon size={ICON_SIZE} /> :
                <IconSun size={ICON_SIZE} />
          }
          onClick={() => {
            setInitialTheme(nextTheme);
            setColorScheme(nextTheme);
          }}
        >
          {nextTheme === "light"
            ? (<FormattedMessage id="menu.lightMode" defaultMessage="Light mode" />)
            : (<FormattedMessage id="menu.darkMode" defaultMessage="Dark mode" />)}
        </Menu.Item>

        <Divider />

        <Menu.Item
          onClick={() => {
            void logout().finally(handleLogout);
          }}
          leftSection={<IconLogout size={ICON_SIZE} />}
        >
          <FormattedMessage id="menu.logOut" defaultMessage="Log out" />
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
